import { Component, Prop, State, Element, Listen, Method, Host, h } from '@stencil/core';
import { breakpoints } from '../../utils/variables';
import { focusElement } from '../../utils/functions';

const RESIZE_DEBOUNCE_TIME = 100; // milliseconds

/**
 * Headers assist users in identifying where they are in a site structure and provide a simple, effective means of reaching main sections of a site via a top level navigation.
 */

/**
 * @slot header-menu - This slot is used for the main horizontal header menu.
 * @slot panel-menu - This slot is used for the fly out panel menu.
 */
@Component({
  tag: 'som-header',
  styleUrl: 'som-header.scss',
  shadow: true,
})
export class SomHeader {
  mainContentElem; // Used when skipping for main content (ADA)

  slots; // Used to track slot updates

  timeout; // Used when window resizes

  updateSlotsCb; // Callback to use when slots content changes

  menuButtonEl; // Hamburger menu button - we need this to gain focus after menu closes.

  // TODO : Evaluate all local variables
  /* come from primary-header */
  menuPanelEl;
  menu;
  mobileMenu;
  desktopMenu;
  menuButtonAriaLabel = 'Open Menu';
  panelMenuSlot;


  @Element() el: HTMLElement;

  /**
   * This property is the name of the application.
   */
  @Prop() appName: string = '';

  /**
   * This property is the name of the department where the application resides.
   */
  @Prop() departmentName: string = '';

  /**
   * This property is the ID of the main content section of the application. It is used to establish a connection for the "skip navigation" link.
   * This is a **required** field
   */
  @Prop() mainContentId: string = '';

  /**
   * This property is the breakpoint where the mobile navigation should take effect.  Setting the value to `"none"` will always show the flyout menu.
   */
  @Prop() breakpoint: 'sm' | 'md' | 'lg' | 'xl' | 'none' = 'lg';

  /**
   * This optional property is used to set a link on the header logo and title text.
   */
  @Prop() homeLink?: string = null;

  /**
   * This boolean determines if the header should stick to the top of scroll with the content
   */
  @Prop() sticky: boolean = false;

  @State() mobileMode: boolean = false;
  @State() hideMobileMenu: boolean = false;
  @State() opened: boolean = false;

  @Listen('resize', { target: 'window' })
  checkBreakpoint() {
    // clear the timeout
    clearTimeout(this.timeout);

    // Setup debounce so we're not constantly reading screen width
    this.timeout = setTimeout(this.checkMobileMode.bind(this), RESIZE_DEBOUNCE_TIME);
  }

  /**
   * This method will close the panel menu if opened. It can be useful when a menu link is clicked but the page doesn't refresh.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  closePanelMenu(): Promise<void> {
    return this.menuPanelEl.close();
  }

  checkMobileMode = () => {
    if (window.innerWidth < breakpoints[this.breakpoint]) {
      // Mobile
      this.mobileMode = true;
    } else {
      // Desktop
      this.mobileMode = false;
    }
  };

  componentWillLoad() {
    if (this.el.children.length === 0) {
      // Header doesn't have menu links
      this.hideMobileMenu = true;
    }

    // Look to see if there is content in the panel menu slot. We'll use this to hide the button.
    this.panelMenuSlot = !!this.el.querySelector('[slot="panel-menu"]');

    // Add tabindex = -1 for IE 11 suppport
    this.mainContentElem = document.getElementById(this.mainContentId);
    if (this.mainContentElem) {
      this.mainContentElem.tabIndex = -1;
    }
  }

  componentDidLoad() {
    this.checkMobileMode();
  }

  toggleMenu() {
    if (this.opened) {
      // Menu is open, so we're closing it
      this.menuPanelEl.close();
      this.opened = false;
    } else {
      // Menu is closed so we're opening it
      this.menuPanelEl.open();
      this.opened = true;
      //      this.menuPanelEl.focus();
    }
  }


  @Listen('menuClosed')
  parentMenuClosedHandler() {
    this.menuPanelEl.refresh();
  }

  @Listen('menuOpened')
  parentMenuOpenHandler() {
    this.menuPanelEl.refresh();
  }

  // The menu has closed, set the open state and move focus to the menu button.
  panelCloseHandler() {
    this.opened = false;
    focusElement(this.menuButtonEl);
  }

  render() {
    return (
      <Host class={`${this.sticky ? 'sticky' : ''}`}>
        {[
          this.mainContentId ? (
            <div class="a11y-skip-link-container">
              <a
                href={`#${this.mainContentId}`}
                tabindex="0"
                class="a11y-skip-link"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  focusElement(this.mainContentElem);
                }}
              >
                Skip to main content
              </a>
            </div>
          ) : null,
          <header class={`header ${this.opened ? 'open' : 'closed'}`} aria-label="main">
            {this.homeLink ? (
              <a href={this.homeLink} class="header__home-link">
                <div
                  class="header__logo"
                  aria-hidden="true"
                  innerHTML={`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 137.05 137.05"><defs><style>.cls-1{fill:url(#linear-gradient);}.cls-2{fill:#fff;}</style><linearGradient id="linear-gradient" x1="1.91" y1="0.81" x2="134.6" y2="135.69" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#287c78"/><stop offset="0.38" stop-color="#1e7a87"/><stop offset="1" stop-color="#0876a7"/></linearGradient></defs><title>Mark_Color</title><g id="Logos"><rect class="cls-1" width="137.05" height="137.05"/><path class="cls-2" d="M85.09,51.46l13,7.63a1.6,1.6,0,0,1,.58,2.18l-.18.31-.9,1.57a.2.2,0,0,0,0,.25L99,64.6l.27.23a.86.86,0,0,1,.28.67l-.32,5.28a3.41,3.41,0,0,1-.92,2.14l-5.5,5.83h0a2.42,2.42,0,0,0-.65,1.72h0l.08,2.93h0a2.44,2.44,0,0,0,1.18,2h0l3,1.75,1,.61A1.18,1.18,0,0,0,99,87.39l.64-1,4.47-7a.77.77,0,0,1,.47-.32l.94-.2a42.65,42.65,0,0,1,4.41,19.5V99a7.74,7.74,0,0,0-5.64,5.15l-1.34,4.08a9.25,9.25,0,0,1-1.68,3.06l-.88,1.08L87.8,113l-.1-.92a.14.14,0,0,0-.15-.12l-2.09.07-20.9.68a6.88,6.88,0,0,0,.43-1.33l.15-.76a9.79,9.79,0,0,0,3.44-5.66l1.22-6a13.82,13.82,0,0,0-1.11-8.77L67,86.7a7,7,0,0,1-.48-4.83l3.84-14.71a4.63,4.63,0,0,1,1.5-2.37l.08-.07.35.6.74,1.28.4.69a1,1,0,0,0,.88.51h1.73a2.28,2.28,0,0,0,1.33-.44l0,0,1.24-.91.32-.23A1.6,1.6,0,0,0,79.63,65l0-.39L80,58.83a.26.26,0,0,1,.17-.24l2.35-.9,2-.78a.39.39,0,0,0,.21-.52l-.91-2-.29-.62a.43.43,0,0,1,.06-.46l1.51-1.88m-.77-2.88L81.5,52.07a2.34,2.34,0,0,0-.3,2.46l.42.91-2.65,1a2.16,2.16,0,0,0-1.39,1.91l-.29,5.23a1.88,1.88,0,0,1-.75,1.4l-.39.28a.55.55,0,0,1-.33.11h-.36a.58.58,0,0,1-.49-.28l-.11-.21a2.33,2.33,0,0,1-.26-1.69l.31-1.35a2.52,2.52,0,0,0-.47-2.14.18.18,0,0,0-.27,0L70.32,63a7.09,7.09,0,0,0-2.27,3.6L64.21,81.26a9.4,9.4,0,0,0,.64,6.49l1.67,3.45a11.32,11.32,0,0,1,.91,7.22l-1.22,6a7.69,7.69,0,0,1-2.66,4.32,2.29,2.29,0,0,0-.76,1.29l-.17.8a4.29,4.29,0,0,1-.87,1.81,2.39,2.39,0,0,0-.37,2.26l.09.26,24.07-.78.11.94a.14.14,0,0,0,.14.13l15.77-.74,1.56-1.91a11.8,11.8,0,0,0,2.1-3.84l1.34-4.08a5.37,5.37,0,0,1,4.66-3.67l.47,0a.67.67,0,0,0,.61-.66l0-2.18a44.88,44.88,0,0,0-4.73-20.76l-.6-1.2a.46.46,0,0,0-.5-.24l-2.79.57a2.31,2.31,0,0,0-1.44,1l-4.38,6.9a.58.58,0,0,1-.78.19l-1.92-1.13a1.23,1.23,0,0,1-.6-1l0-1.9a.89.89,0,0,1,.23-.62L100,74.58a5.83,5.83,0,0,0,1.58-3.65l.32-5.29A3.19,3.19,0,0,0,100.81,63l-.27-.24.18-.3A4,4,0,0,0,99.28,57L84.77,48.47l-.14-.08a.15.15,0,0,0-.19,0Z"/><path class="cls-2" d="M46.92,23.78a3.06,3.06,0,0,0-.47.78l-2.3,5.22L42.8,32.84a.53.53,0,0,0,.55.74l3.32-.44,2.86-.38.34,0A2.59,2.59,0,0,1,52,33.89l2.12,3.2.76,1.14a.53.53,0,0,0,.58.22l1.32-.37,2-.58a2.16,2.16,0,0,1,.62-.09,2.22,2.22,0,0,1,1.25.38l1.43.93,1.4.92a.54.54,0,0,0,.7-.1l1.07-1.3.9-1.07a5.29,5.29,0,0,1,4.08-1.92h0l5.55.06h0A3.87,3.87,0,0,0,78.88,34l0,0A.16.16,0,0,1,79,33.9h0l.76,0-.09.5A3.12,3.12,0,0,0,82.44,38l4.8.53a.86.86,0,0,1,.73.61l.27.89a7.24,7.24,0,0,0,.88,1.89l-2-.11H87a3.58,3.58,0,0,0-2.87,1.43,5,5,0,0,0-3.7-1.92l-2.65-.13h-.16a3.4,3.4,0,0,0-3.08,2,.32.32,0,0,1-.28.19l-3.34.11a5.19,5.19,0,0,0-3.42,1.44l-4.2,4-.4-.11-.44-1.38-.52-1.64a.52.52,0,0,0-.61-.35l-1.68.36-.76.17a3.1,3.1,0,0,0-2.33,2.2l-.14.53a19.9,19.9,0,0,1-2.1,4.93,5.79,5.79,0,0,0-1-1.67l0-.76a4.94,4.94,0,0,0-3.23-4.94l-.36-.14a5.52,5.52,0,0,0-5.28-4,5.75,5.75,0,0,0-1.24.14l-1,.23L27.94,37.19a5.25,5.25,0,0,0-.69-1.25,6.66,6.66,0,0,0,3.82-3l2.18,0a6.12,6.12,0,0,0,4.87-2.55l.17-.24A2.55,2.55,0,0,0,41,27.84l6-4.06Zm1.34-3.34a2.31,2.31,0,0,0-1.31.41l-7.34,5a2.37,2.37,0,0,0-.93,1.21c-.18.56-.43.73-1.07.73a1.17,1.17,0,0,0-1,.48l-.49.68a3.64,3.64,0,0,1-2.94,1.54l-3.3.05a.53.53,0,0,0-.48.3l-.31.63A4.28,4.28,0,0,1,26,33.77l-2.6.39a.2.2,0,0,0-.16.21l.08,1.18a1,1,0,0,0,.56.8l.44.2a2.81,2.81,0,0,1,1.54,1.87l.1.42a.37.37,0,0,0,.22.25L41.91,45a.56.56,0,0,0,.36,0l1.48-.35a3.42,3.42,0,0,1,.7-.08,3.1,3.1,0,0,1,3,2.63l.08.56a.29.29,0,0,0,.18.22l1.5.56a2.53,2.53,0,0,1,1.65,2.53l-.11,1.77a.26.26,0,0,0,.1.2h0a3.41,3.41,0,0,1,1.29,2.06l.11.53A3.63,3.63,0,0,0,54,58h0a.14.14,0,0,0,.18,0l.69-.89a22.53,22.53,0,0,0,3.85-7.73l.14-.53a.7.7,0,0,1,.51-.49l.76-.16.6,1.88a1.46,1.46,0,0,0,1,1l1.37.37a1.51,1.51,0,0,0,.41.06A1.61,1.61,0,0,0,64.58,51l4.54-4.35A2.78,2.78,0,0,1,71,45.88l4.22-.14a1.55,1.55,0,0,0,1.5-1.55h0a1,1,0,0,1,.9-.57h.05l2.65.12a2.58,2.58,0,0,1,2.15,1.38l0,.07a1.86,1.86,0,0,0,1.65,1h1.37a.17.17,0,0,0,.16-.14l.15-.84a1.2,1.2,0,0,1,1.17-1H87l6.34.33a.23.23,0,0,0,.2-.11l0,0a1.46,1.46,0,0,0-.42-2l-.75-.52a4.82,4.82,0,0,1-1.86-2.57l-.27-.89a3.26,3.26,0,0,0-2.78-2.32l-4.8-.53a.69.69,0,0,1-.61-.81l.47-3a.21.21,0,0,0-.2-.25l-3.26-.1H79a2.53,2.53,0,0,0-1.9.86l0,0a1.5,1.5,0,0,1-1.14.51h0l-5.54-.06h-.08a7.73,7.73,0,0,0-5.94,2.79l-.89,1.07-1.42-.93a4.69,4.69,0,0,0-3.86-.59l-2,.57L54,32.56a5,5,0,0,0-4.17-2.23,4.87,4.87,0,0,0-.66,0l-2.85.38,2.3-5.22a1,1,0,0,1,.81-.6,3.28,3.28,0,0,0,2.36-1.5l1.27-2a.27.27,0,0,0-.19-.4l-4.33-.59-.32,0Z"/></g></svg>`}
                ></div>
                <div class="header__title">
                  <div class="header__title-application">{this.appName}</div>
                  {this.departmentName ? <div class="header__title-department">{this.departmentName}</div> : null}
                </div>
              </a>
            ) : (
              [
                <div
                  class="header__logo"
                  innerHTML={`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 137.05 137.05"><defs><style>.cls-1{fill:url(#linear-gradient);}.cls-2{fill:#fff;}</style><linearGradient id="linear-gradient" x1="1.91" y1="0.81" x2="134.6" y2="135.69" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#287c78"/><stop offset="0.38" stop-color="#1e7a87"/><stop offset="1" stop-color="#0876a7"/></linearGradient></defs><title>Mark_Color</title><g id="Logos"><rect class="cls-1" width="137.05" height="137.05"/><path class="cls-2" d="M85.09,51.46l13,7.63a1.6,1.6,0,0,1,.58,2.18l-.18.31-.9,1.57a.2.2,0,0,0,0,.25L99,64.6l.27.23a.86.86,0,0,1,.28.67l-.32,5.28a3.41,3.41,0,0,1-.92,2.14l-5.5,5.83h0a2.42,2.42,0,0,0-.65,1.72h0l.08,2.93h0a2.44,2.44,0,0,0,1.18,2h0l3,1.75,1,.61A1.18,1.18,0,0,0,99,87.39l.64-1,4.47-7a.77.77,0,0,1,.47-.32l.94-.2a42.65,42.65,0,0,1,4.41,19.5V99a7.74,7.74,0,0,0-5.64,5.15l-1.34,4.08a9.25,9.25,0,0,1-1.68,3.06l-.88,1.08L87.8,113l-.1-.92a.14.14,0,0,0-.15-.12l-2.09.07-20.9.68a6.88,6.88,0,0,0,.43-1.33l.15-.76a9.79,9.79,0,0,0,3.44-5.66l1.22-6a13.82,13.82,0,0,0-1.11-8.77L67,86.7a7,7,0,0,1-.48-4.83l3.84-14.71a4.63,4.63,0,0,1,1.5-2.37l.08-.07.35.6.74,1.28.4.69a1,1,0,0,0,.88.51h1.73a2.28,2.28,0,0,0,1.33-.44l0,0,1.24-.91.32-.23A1.6,1.6,0,0,0,79.63,65l0-.39L80,58.83a.26.26,0,0,1,.17-.24l2.35-.9,2-.78a.39.39,0,0,0,.21-.52l-.91-2-.29-.62a.43.43,0,0,1,.06-.46l1.51-1.88m-.77-2.88L81.5,52.07a2.34,2.34,0,0,0-.3,2.46l.42.91-2.65,1a2.16,2.16,0,0,0-1.39,1.91l-.29,5.23a1.88,1.88,0,0,1-.75,1.4l-.39.28a.55.55,0,0,1-.33.11h-.36a.58.58,0,0,1-.49-.28l-.11-.21a2.33,2.33,0,0,1-.26-1.69l.31-1.35a2.52,2.52,0,0,0-.47-2.14.18.18,0,0,0-.27,0L70.32,63a7.09,7.09,0,0,0-2.27,3.6L64.21,81.26a9.4,9.4,0,0,0,.64,6.49l1.67,3.45a11.32,11.32,0,0,1,.91,7.22l-1.22,6a7.69,7.69,0,0,1-2.66,4.32,2.29,2.29,0,0,0-.76,1.29l-.17.8a4.29,4.29,0,0,1-.87,1.81,2.39,2.39,0,0,0-.37,2.26l.09.26,24.07-.78.11.94a.14.14,0,0,0,.14.13l15.77-.74,1.56-1.91a11.8,11.8,0,0,0,2.1-3.84l1.34-4.08a5.37,5.37,0,0,1,4.66-3.67l.47,0a.67.67,0,0,0,.61-.66l0-2.18a44.88,44.88,0,0,0-4.73-20.76l-.6-1.2a.46.46,0,0,0-.5-.24l-2.79.57a2.31,2.31,0,0,0-1.44,1l-4.38,6.9a.58.58,0,0,1-.78.19l-1.92-1.13a1.23,1.23,0,0,1-.6-1l0-1.9a.89.89,0,0,1,.23-.62L100,74.58a5.83,5.83,0,0,0,1.58-3.65l.32-5.29A3.19,3.19,0,0,0,100.81,63l-.27-.24.18-.3A4,4,0,0,0,99.28,57L84.77,48.47l-.14-.08a.15.15,0,0,0-.19,0Z"/><path class="cls-2" d="M46.92,23.78a3.06,3.06,0,0,0-.47.78l-2.3,5.22L42.8,32.84a.53.53,0,0,0,.55.74l3.32-.44,2.86-.38.34,0A2.59,2.59,0,0,1,52,33.89l2.12,3.2.76,1.14a.53.53,0,0,0,.58.22l1.32-.37,2-.58a2.16,2.16,0,0,1,.62-.09,2.22,2.22,0,0,1,1.25.38l1.43.93,1.4.92a.54.54,0,0,0,.7-.1l1.07-1.3.9-1.07a5.29,5.29,0,0,1,4.08-1.92h0l5.55.06h0A3.87,3.87,0,0,0,78.88,34l0,0A.16.16,0,0,1,79,33.9h0l.76,0-.09.5A3.12,3.12,0,0,0,82.44,38l4.8.53a.86.86,0,0,1,.73.61l.27.89a7.24,7.24,0,0,0,.88,1.89l-2-.11H87a3.58,3.58,0,0,0-2.87,1.43,5,5,0,0,0-3.7-1.92l-2.65-.13h-.16a3.4,3.4,0,0,0-3.08,2,.32.32,0,0,1-.28.19l-3.34.11a5.19,5.19,0,0,0-3.42,1.44l-4.2,4-.4-.11-.44-1.38-.52-1.64a.52.52,0,0,0-.61-.35l-1.68.36-.76.17a3.1,3.1,0,0,0-2.33,2.2l-.14.53a19.9,19.9,0,0,1-2.1,4.93,5.79,5.79,0,0,0-1-1.67l0-.76a4.94,4.94,0,0,0-3.23-4.94l-.36-.14a5.52,5.52,0,0,0-5.28-4,5.75,5.75,0,0,0-1.24.14l-1,.23L27.94,37.19a5.25,5.25,0,0,0-.69-1.25,6.66,6.66,0,0,0,3.82-3l2.18,0a6.12,6.12,0,0,0,4.87-2.55l.17-.24A2.55,2.55,0,0,0,41,27.84l6-4.06Zm1.34-3.34a2.31,2.31,0,0,0-1.31.41l-7.34,5a2.37,2.37,0,0,0-.93,1.21c-.18.56-.43.73-1.07.73a1.17,1.17,0,0,0-1,.48l-.49.68a3.64,3.64,0,0,1-2.94,1.54l-3.3.05a.53.53,0,0,0-.48.3l-.31.63A4.28,4.28,0,0,1,26,33.77l-2.6.39a.2.2,0,0,0-.16.21l.08,1.18a1,1,0,0,0,.56.8l.44.2a2.81,2.81,0,0,1,1.54,1.87l.1.42a.37.37,0,0,0,.22.25L41.91,45a.56.56,0,0,0,.36,0l1.48-.35a3.42,3.42,0,0,1,.7-.08,3.1,3.1,0,0,1,3,2.63l.08.56a.29.29,0,0,0,.18.22l1.5.56a2.53,2.53,0,0,1,1.65,2.53l-.11,1.77a.26.26,0,0,0,.1.2h0a3.41,3.41,0,0,1,1.29,2.06l.11.53A3.63,3.63,0,0,0,54,58h0a.14.14,0,0,0,.18,0l.69-.89a22.53,22.53,0,0,0,3.85-7.73l.14-.53a.7.7,0,0,1,.51-.49l.76-.16.6,1.88a1.46,1.46,0,0,0,1,1l1.37.37a1.51,1.51,0,0,0,.41.06A1.61,1.61,0,0,0,64.58,51l4.54-4.35A2.78,2.78,0,0,1,71,45.88l4.22-.14a1.55,1.55,0,0,0,1.5-1.55h0a1,1,0,0,1,.9-.57h.05l2.65.12a2.58,2.58,0,0,1,2.15,1.38l0,.07a1.86,1.86,0,0,0,1.65,1h1.37a.17.17,0,0,0,.16-.14l.15-.84a1.2,1.2,0,0,1,1.17-1H87l6.34.33a.23.23,0,0,0,.2-.11l0,0a1.46,1.46,0,0,0-.42-2l-.75-.52a4.82,4.82,0,0,1-1.86-2.57l-.27-.89a3.26,3.26,0,0,0-2.78-2.32l-4.8-.53a.69.69,0,0,1-.61-.81l.47-3a.21.21,0,0,0-.2-.25l-3.26-.1H79a2.53,2.53,0,0,0-1.9.86l0,0a1.5,1.5,0,0,1-1.14.51h0l-5.54-.06h-.08a7.73,7.73,0,0,0-5.94,2.79l-.89,1.07-1.42-.93a4.69,4.69,0,0,0-3.86-.59l-2,.57L54,32.56a5,5,0,0,0-4.17-2.23,4.87,4.87,0,0,0-.66,0l-2.85.38,2.3-5.22a1,1,0,0,1,.81-.6,3.28,3.28,0,0,0,2.36-1.5l1.27-2a.27.27,0,0,0-.19-.4l-4.33-.59-.32,0Z"/></g></svg>`}
                ></div>,
                <div class="header__title">
                  <div class="header__title-application">{this.appName}</div>
                  {this.departmentName ? <div class="header__title-department">{this.departmentName}</div> : null}
                </div>,
              ]
            )}

            <div class="header__menu">
              <div class="header__menu-inline">
                <slot name="header-menu"></slot>
              </div>
              {this.panelMenuSlot ? (
                <div class={`header__menu-button-container ${this.breakpoint ? `header__menu-button-container__display-${this.breakpoint}` : ''}`}>
                  <button
                    type="button"
                    class="header__menu-button"
                    aria-label={this.menuButtonAriaLabel}
                    onClick={() => this.toggleMenu()}
                    ref={el => {
                      this.menuButtonEl = el;
                    }}
                  >
                    {this.opened ? <som-icon name="close" display="block"></som-icon> : <som-icon name="menu" display="block"></som-icon>}
                  </button>
                </div>
              ) : null}
            </div>
          </header>,
          this.panelMenuSlot ? (
            <som-panel
              id="menu-panel"
              class='header-panel'
              position="right"
              size="md"
              breakpoint="md"
              hide-close-button="false"
              below-header="true"
              ref={el => {
                this.menuPanelEl = el;
              }}
              onBeforeClose={() => {
                this.panelCloseHandler();
              }}
            >
              <slot name="panel-menu"></slot>
            </som-panel>
          ) : null
          ]}
      </Host>
    )
  }
}
